import React, {useEffect, useMemo, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";

import {borderRadius, calculateRemSize, display, flexAbsoluteCenter} from "@pg-design/helpers-css";
import {Modal, useModal} from "@pg-design/modal-module";
import {noop, rearrangeArray} from "@pg-mono/nodash";
import {SliderType} from "@pg-mono/slick-slider";
import {useUserDevice} from "@pg-mono/user-device";

import {useReelUrlSync} from "../../hooks/use_reel_url_sync";
import {useReelsNavigation} from "../../hooks/use_reels_navigation";
import {useUserReelStats} from "../../hooks/use_user_reel_stats";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelListItem} from "../../types/IReelListItem";
import {reelSliderTrackTeasingAnimation} from "../../utils/reel_slider_track_teasing_animation";
import {ReelDetailsFullscreen} from "../details/ReelDetailsFullscreen";
import {ReelModalCloseButton} from "./ReelModalCloseButton";
import {ReelNavigationButtons} from "./ReelNavigationButtons";
import {ReelSlider} from "./ReelSlider";
import {ReelSliderSlide} from "./ReelSliderSlide";

interface IProps {
    modalInstance: ReturnType<typeof useModal<{currentReelId?: number}>>;
    reels: IReelListItem[];
}

export const ReelModal = (props: IProps) => {
    const {modalInstance} = props;
    const {isMobile} = useUserDevice();
    const [disableNavigation, setDisableNavigation] = useState(false);
    const [currentReelId, setCurrentReelId] = useState<number | null>(null);
    const [hasFirstSlideBeenInitialized, setHasFirstSlideBeenInitialized] = useState(false);
    // TODO: enable teasing animation - needs some performance improvements
    const hasBeenTeased = useRef(true);
    const sliderRef = useRef<SliderType | null>(null);
    const {syncReelUrl, createReelFullUrl} = useReelUrlSync();
    const {currentSessionReelStats, resetCurrentSessionReelStats, increaseReelPlayNumber} = useUserReelStats();

    const defaultReelId = useMemo(() => {
        if (modalInstance.isModalRendered) {
            return modalInstance.modalProps.customParams?.currentReelId || props.reels[0]?.id;
        }

        return null;
    }, [modalInstance.isModalRendered]);

    const reels = useMemo(() => {
        return rearrangeArray(props.reels, (reel) => reel.id === defaultReelId);
    }, [!!defaultReelId]);

    const onNextReel = async () => {
        if (hasEveryReelBeenPlayed()) {
            props.modalInstance.modalProps.onModalClose();
        } else {
            sliderRef.current?.slickNext();
        }
    };

    const onPrevReel = () => {
        sliderRef.current?.slickPrev();
    };

    const {scrollableWrapperRef} = useReelsNavigation({
        onNextReel,
        onPrevReel,
        disableNavigation: !props.modalInstance.modalProps.isOpen || disableNavigation
    });

    useEffect(() => {
        if (defaultReelId && sliderRef.current) {
            resetCurrentSessionReelStats();
            syncReelUrl(defaultReelId);
        }
    }, [defaultReelId, sliderRef.current]);

    useEffect(() => {
        if (!defaultReelId || !scrollableWrapperRef?.current || !hasFirstSlideBeenInitialized) {
            return;
        }

        const init = () => {
            hasBeenTeased.current = true;
            setCurrentReelId(defaultReelId);
            increaseReelPlayNumber(defaultReelId);
        };

        if (hasBeenTeased.current) {
            init();
        } else {
            const timeout = setTimeout(() => {
                reelSliderTrackTeasingAnimation({slickSliderWrapperRef: scrollableWrapperRef}).then(init);
            }, 500);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [!!defaultReelId, scrollableWrapperRef.current, hasFirstSlideBeenInitialized]);

    const handleSliderAfterChange = (slideIndex: number) => {
        const reel = reels[slideIndex];
        setCurrentReelId(reel.id);
        increaseReelPlayNumber(reel.id);
    };

    const hasEveryReelBeenPlayed = () => {
        const reelIds = reels.map((reel) => reel.id);
        const playedReelIds = Object.keys(currentSessionReelStats).map(Number);

        return reelIds.every((reelId) => playedReelIds.includes(reelId));
    };

    const onVideoEnded = () => {
        if (hasEveryReelBeenPlayed()) {
            props.modalInstance.modalProps.onModalClose();
        } else {
            onNextReel();
        }
    };

    useEffect(() => {
        if (sliderRef.current) {
            // ugly hack to enable vertical swiping, waiting for new version of slick-slider
            // https://github.com/akiran/react-slick/pull/2398
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (sliderRef.current.innerSlider as any)?.setState({verticalSwiping: true});
        }
    }, [sliderRef.current]);

    const onSlideReady = () => {
        setHasFirstSlideBeenInitialized(true);
    };

    const onArrowUpClick = () => {
        reelsAnalytics.gtm.reelNavigationEvent();
        onPrevReel();
    };

    const onArrowDownClick = () => {
        reelsAnalytics.gtm.reelNavigationEvent();
        onNextReel();
    };

    const onCloseBtnClick = () => {
        reelsAnalytics.gtm.reelCloseEvent();
        props.modalInstance.modalProps.onModalClose();
    };

    const handleDisableReelsNavigation = () => {
        setDisableNavigation(true);
    };

    const handleEnableReelsNavigation = () => {
        setDisableNavigation(false);
    };

    useEffect(() => {
        // FIXME: ugly, temporary fix for disabling navigation inside react-slick
        // Problem: on other modal open, touch events are still captured by the slider
        if (disableNavigation && sliderRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const innerSlider = sliderRef.current.innerSlider as any;
            const oldSwipeMove = innerSlider.swipeMove;
            innerSlider.swipeMove = noop;

            return () => {
                innerSlider.swipeMove = oldSwipeMove;
            };
        }
    }, [disableNavigation]);

    return (
        <Modal {...props.modalInstance.modalProps} shouldCloseOnOverlayClick={false} borderRadius={0} css={modalStyle} overlayStyle={modalOverlayStyle}>
            <div ref={scrollableWrapperRef} css={sliderWrapperStyle}>
                <ReelSlider ready={props.modalInstance.isModalRendered} ref={sliderRef} reelsCount={reels.length} afterChange={handleSliderAfterChange}>
                    {reels.map((reel, index) => (
                        <ReelSliderSlide key={reel.id} isActive={reel.id === currentReelId}>
                            {({isVisible}) => (
                                <ReelDetailsFullscreen
                                    isActive={isVisible && reel.id === currentReelId}
                                    reelId={reel.id}
                                    reelListIndex={index}
                                    reelDetails={reel}
                                    disableFetch={!isVisible}
                                    shareUrl={createReelFullUrl(reel.id)}
                                    showCloseBtn={!!isMobile}
                                    onReady={onSlideReady}
                                    onClose={onCloseBtnClick}
                                    onVideoEnded={onVideoEnded}
                                    handleDisableReelsNavigation={handleDisableReelsNavigation}
                                    handleEnableReelsNavigation={handleEnableReelsNavigation}
                                    css={reelDetailsStyle}
                                />
                            )}
                        </ReelSliderSlide>
                    ))}
                </ReelSlider>
            </div>
            <div css={navigationStyle}>
                <ReelModalCloseButton onClick={onCloseBtnClick} />
                <ReelNavigationButtons onUpClick={onArrowUpClick} onDownClick={onArrowDownClick} css={reels.length < 2 && display("none")} />
            </div>
        </Modal>
    );
};

const modalStyle = css`
    background-color: transparent;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
`;

const modalOverlayStyle = {
    backgroundColor: "rgba(35, 35, 45, 0.9)"
};

const sliderWrapperStyle = css`
    flex: 0 0 auto;
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;

    .slick-slider {
        width: 100vw;
        height: 100vh;
    }

    .slick-slide {
        border: none;
    }
`;

const navigationStyle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.sm}) {
        display: block;
        position: absolute;
        inset: ${calculateRemSize(4)} ${calculateRemSize(4)} ${calculateRemSize(4)} auto;
        ${flexAbsoluteCenter};
    }
`;

const reelDetailsStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        ${borderRadius(2)};
    }
`;
import React, {useEffect, useMemo, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";

import {borderRadius, calculateRemSize, display, flexAbsoluteCenter} from "@pg-design/helpers-css";
import {Modal, useModal} from "@pg-design/modal-module";
import {noop, rearrangeArray} from "@pg-mono/nodash";
import {SliderType} from "@pg-mono/slick-slider";
import {useUserDevice} from "@pg-mono/user-device";

import {useReelUrlSync} from "../../hooks/use_reel_url_sync";
import {useReelsNavigation} from "../../hooks/use_reels_navigation";
import {useUserReelStats} from "../../hooks/use_user_reel_stats";
import {reelsAnalytics} from "../../tracking/reels_gtm_events";
import {IReelListItem} from "../../types/IReelListItem";
import {reelSliderTrackTeasingAnimation} from "../../utils/reel_slider_track_teasing_animation";
import {ReelDetailsFullscreen} from "../details/ReelDetailsFullscreen";
import {ReelModalCloseButton} from "./ReelModalCloseButton";
import {ReelNavigationButtons} from "./ReelNavigationButtons";
import {ReelSlider} from "./ReelSlider";
import {ReelSliderSlide} from "./ReelSliderSlide";

interface IProps {
    modalInstance: ReturnType<typeof useModal<{currentReelId?: number}>>;
    reels: IReelListItem[];
}

export const ReelModal = (props: IProps) => {
    const {modalInstance} = props;
    const {isMobile} = useUserDevice();
    const [disableNavigation, setDisableNavigation] = useState(false);
    const [currentReelId, setCurrentReelId] = useState<number | null>(null);
    const [hasFirstSlideBeenInitialized, setHasFirstSlideBeenInitialized] = useState(false);
    // TODO: enable teasing animation - needs some performance improvements
    const hasBeenTeased = useRef(true);
    const sliderRef = useRef<SliderType | null>(null);
    const {syncReelUrl, createReelFullUrl} = useReelUrlSync();
    const {currentSessionReelStats, resetCurrentSessionReelStats, increaseReelPlayNumber} = useUserReelStats();

    const defaultReelId = useMemo(() => {
        if (modalInstance.isModalRendered) {
            return modalInstance.modalProps.customParams?.currentReelId || props.reels[0]?.id;
        }

        return null;
    }, [modalInstance.isModalRendered]);

    const reels = useMemo(() => {
        return rearrangeArray(props.reels, (reel) => reel.id === defaultReelId);
    }, [!!defaultReelId]);

    const onNextReel = async () => {
        if (hasEveryReelBeenPlayed()) {
            props.modalInstance.modalProps.onModalClose();
        } else {
            sliderRef.current?.slickNext();
        }
    };

    const onPrevReel = () => {
        sliderRef.current?.slickPrev();
    };

    const {scrollableWrapperRef} = useReelsNavigation({
        onNextReel,
        onPrevReel,
        disableNavigation: !props.modalInstance.modalProps.isOpen || disableNavigation
    });

    useEffect(() => {
        if (defaultReelId && sliderRef.current) {
            resetCurrentSessionReelStats();
            syncReelUrl(defaultReelId);
        }
    }, [defaultReelId, sliderRef.current]);

    useEffect(() => {
        if (!defaultReelId || !scrollableWrapperRef?.current || !hasFirstSlideBeenInitialized) {
            return;
        }

        const init = () => {
            hasBeenTeased.current = true;
            setCurrentReelId(defaultReelId);
            increaseReelPlayNumber(defaultReelId);
        };

        if (hasBeenTeased.current) {
            init();
        } else {
            const timeout = setTimeout(() => {
                reelSliderTrackTeasingAnimation({slickSliderWrapperRef: scrollableWrapperRef}).then(init);
            }, 500);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [!!defaultReelId, scrollableWrapperRef.current, hasFirstSlideBeenInitialized]);

    const handleSliderAfterChange = (slideIndex: number) => {
        const reel = reels[slideIndex];
        setCurrentReelId(reel.id);
        increaseReelPlayNumber(reel.id);
    };

    const hasEveryReelBeenPlayed = () => {
        const reelIds = reels.map((reel) => reel.id);
        const playedReelIds = Object.keys(currentSessionReelStats).map(Number);

        return reelIds.every((reelId) => playedReelIds.includes(reelId));
    };

    const onVideoEnded = () => {
        if (hasEveryReelBeenPlayed()) {
            props.modalInstance.modalProps.onModalClose();
        } else {
            onNextReel();
        }
    };

    useEffect(() => {
        if (sliderRef.current) {
            // ugly hack to enable vertical swiping, waiting for new version of slick-slider
            // https://github.com/akiran/react-slick/pull/2398
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (sliderRef.current.innerSlider as any)?.setState({verticalSwiping: true});
        }
    }, [sliderRef.current]);

    const onSlideReady = () => {
        setHasFirstSlideBeenInitialized(true);
    };

    const onArrowUpClick = () => {
        reelsAnalytics.gtm.reelNavigationEvent();
        onPrevReel();
    };

    const onArrowDownClick = () => {
        reelsAnalytics.gtm.reelNavigationEvent();
        onNextReel();
    };

    const onCloseBtnClick = () => {
        reelsAnalytics.gtm.reelCloseEvent();
        props.modalInstance.modalProps.onModalClose();
    };

    const handleDisableReelsNavigation = () => {
        setDisableNavigation(true);
    };

    const handleEnableReelsNavigation = () => {
        setDisableNavigation(false);
    };

    useEffect(() => {
        // FIXME: ugly, temporary fix for disabling navigation inside react-slick
        // Problem: on other modal open, touch events are still captured by the slider
        if (disableNavigation && sliderRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const innerSlider = sliderRef.current.innerSlider as any;
            const oldSwipeMove = innerSlider.swipeMove;
            innerSlider.swipeMove = noop;

            return () => {
                innerSlider.swipeMove = oldSwipeMove;
            };
        }
    }, [disableNavigation]);

    return (
        <Modal {...props.modalInstance.modalProps} shouldCloseOnOverlayClick={false} borderRadius={0} css={modalStyle} overlayStyle={modalOverlayStyle}>
            <div ref={scrollableWrapperRef} css={sliderWrapperStyle}>
                <ReelSlider ready={props.modalInstance.isModalRendered} ref={sliderRef} reelsCount={reels.length} afterChange={handleSliderAfterChange}>
                    {reels.map((reel, index) => (
                        <ReelSliderSlide key={reel.id} isActive={reel.id === currentReelId}>
                            {({isVisible}) => (
                                <ReelDetailsFullscreen
                                    isActive={isVisible && reel.id === currentReelId}
                                    reelId={reel.id}
                                    reelListIndex={index}
                                    reelDetails={reel}
                                    disableFetch={!isVisible}
                                    shareUrl={createReelFullUrl(reel.id)}
                                    showCloseBtn={!!isMobile}
                                    onReady={onSlideReady}
                                    onClose={onCloseBtnClick}
                                    onVideoEnded={onVideoEnded}
                                    handleDisableReelsNavigation={handleDisableReelsNavigation}
                                    handleEnableReelsNavigation={handleEnableReelsNavigation}
                                    css={reelDetailsStyle}
                                />
                            )}
                        </ReelSliderSlide>
                    ))}
                </ReelSlider>
            </div>
            <div css={navigationStyle}>
                <ReelModalCloseButton onClick={onCloseBtnClick} />
                <ReelNavigationButtons onUpClick={onArrowUpClick} onDownClick={onArrowDownClick} css={reels.length < 2 && display("none")} />
            </div>
        </Modal>
    );
};

const modalStyle = css`
    background-color: transparent;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
`;

const modalOverlayStyle = {
    backgroundColor: "rgba(35, 35, 45, 0.9)"
};

const sliderWrapperStyle = css`
    flex: 0 0 auto;
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 100%;

    .slick-slider {
        width: 100vw;
        height: 100vh;
    }

    .slick-slide {
        border: none;
    }
`;

const navigationStyle = (theme: Theme) => css`
    display: none;

    @media (min-width: ${theme.breakpoints.sm}) {
        display: block;
        position: absolute;
        inset: ${calculateRemSize(4)} ${calculateRemSize(4)} ${calculateRemSize(4)} auto;
        ${flexAbsoluteCenter};
    }
`;

const reelDetailsStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        ${borderRadius(2)};
    }
`;
